import { keepPreviousData, type QueryClient, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { AnalyticValues } from "../contexts/analytics/hooks";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";
import type { AffectedShift, PtoTrimmedSegmentsResponse, TimeOffImpactResponse, TimeOffWindow } from "../utils/models";
import { useDebouncedValue } from "./useDebouncedValue";

export const TIME_OFF_IMPACT_QUERY_KEY_ROOT = "time-off-impact";
export const PTO_TRIMMED_SEGMENTS_QUERY_KEY_ROOT = "pto-trimmed-segments";

// The date pickers move in discrete jumps rather than per keystroke, so a shorter coalescing window
// than the shared hook's 500ms default keeps the preview feeling immediate while still collapsing a
// drag across a month into one request.
const WINDOW_DEBOUNCE_MS = 250;

/**
 * How long the SAVE path will wait for the impact preview before giving up on it.
 *
 * `axiosInstance` is created with no `timeout` (`networkWrapper.tsx`), so a hung server produces a
 * promise that never settles rather than a rejection — without an explicit deadline the save would
 * wait forever and the PTO would silently never be written. Generous enough that a merely slow
 * answer is still used, short enough that the fallback resync is reached while the user is still
 * watching the save spinner.
 */
export const SAVE_PATH_IMPACT_DEADLINE_MS = 4000;

// A `DateTime` pair is a new object on every render, which would thrash both the debounce and the
// query cache — the window travels as a single joined ISO string so identity is value-based.
const WINDOW_KEY_SEPARATOR = "|";

// Structurally `useAnalytics().trackError`, which is not exported as a named type.
type TrackErrorFn = (event: string, props?: AnalyticValues) => void;

/**
 * Serialize a window into a stable cache/debounce key, or null when it cannot be previewed.
 *
 * Equal bounds are rejected by the server (`invalid.date.range`) and an invalid `DateTime`
 * serializes to null, so both are screened out here rather than sent.
 */
const windowKeyOf = (timeOffWindow: TimeOffWindow | null): string | null => {
	if (!timeOffWindow || timeOffWindow.end <= timeOffWindow.start) return null;
	const startIso = timeOffWindow.start.toISO();
	const endIso = timeOffWindow.end.toISO();
	if (!startIso || !endIso) return null;
	return `${startIso}${WINDOW_KEY_SEPARATOR}${endIso}`;
};

const splitWindowKey = (key: string): { startIso: string; endIso: string } => {
	const [startIso, endIso] = key.split(WINDOW_KEY_SEPARATOR);
	return { startIso, endIso };
};

// Stands in for the window while the query is disabled, so the key keeps a stable array shape.
const NO_WINDOW_KEY = WINDOW_KEY_SEPARATOR;

/**
 * Shared query definition so the live preview and the save path's `fetchQuery` hit the same cache
 * entry — the save must never re-derive the window differently from what the user was shown.
 *
 * `trackError` is a parameter rather than a hook call so that BOTH callers register the identical
 * `queryFn` against the key. Two different functions on one key means last-observer-wins, which
 * would let a live preview silently run an untracked fetch.
 */
export const timeOffImpactQueryOptions = (departmentId: string, nurseId: number, windowKey: string, trackError: TrackErrorFn) => {
	const { startIso, endIso } = splitWindowKey(windowKey);
	return queryOptions({
		queryKey: [TIME_OFF_IMPACT_QUERY_KEY_ROOT, departmentId, nurseId, startIso, endIso],
		queryFn: async (): Promise<TimeOffImpactResponse> => {
			try {
				const { data } = await backendService.getTimeOffImpact(departmentId, nurseId, startIso, endIso);
				return data;
			} catch (error) {
				trackError("time_off_impact_fetch_error", {
					error_message: error instanceof Error ? error.message : String(error),
					department_id: departmentId,
					nurse_id: nurseId,
				});
				throw error;
			}
		},
		// Stated rather than inherited: the save path reads this same entry to decide which shifts to
		// drop from local state, and stale ids there leave a deleted shift on the grid. Pinning it
		// here keeps that guarantee independent of the app-wide default someone else owns.
		staleTime: 0,
	});
};

/** Build the save path's window key from the exact (un-debounced) window the user committed. */
export const timeOffImpactWindowKey = (timeOffWindow: TimeOffWindow): string | null => windowKeyOf(timeOffWindow);

/**
 * The impact preview as the SAVE path needs it: bounded, and never retried.
 *
 * Differs from the live preview on two counts, both because a save is waiting on it:
 * - `retry: false` overrides the app-wide `retry: 2` (`index.tsx`). A 400/403 here is a permanent
 *   answer, so retrying only spends the backoff (~1s then ~2s) before the caller's fallback runs.
 * - An explicit deadline, because a hung server never rejects (see `SAVE_PATH_IMPACT_DEADLINE_MS`).
 *
 * Rejects on failure or deadline; the caller is expected to fall back rather than block the save.
 */
export const fetchTimeOffImpactForSave = async (
	queryClient: QueryClient,
	departmentId: string,
	nurseId: number,
	windowKey: string,
	trackError: TrackErrorFn,
): Promise<TimeOffImpactResponse> => {
	let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
	const deadline = new Promise<never>((_, reject) => {
		deadlineTimer = setTimeout(() => reject(new Error(`Time-off impact preview exceeded ${SAVE_PATH_IMPACT_DEADLINE_MS}ms`)), SAVE_PATH_IMPACT_DEADLINE_MS);
	});

	try {
		return await Promise.race([queryClient.fetchQuery({ ...timeOffImpactQueryOptions(departmentId, nurseId, windowKey, trackError), retry: false }), deadline]);
	} finally {
		clearTimeout(deadlineTimer);
	}
};

/**
 * Live preview of what saving a time off over `timeOffWindow` would do to the nurse's scheduled shifts.
 *
 * The overlap rules live only on the server (`utils.py::classify_time_off_overlap`) — callers read
 * the classification back and must never re-derive it.
 *
 * Advisory by design: a slow or failed preview never blocks the save. `keepPreviousData` means a
 * window change re-renders the previous answer rather than blanking, so callers gate skeletons on
 * `isLoading` (genuinely the first fetch) and dim on `isRefreshing`.
 */
export const useTimeOffImpact = (departmentId: string, nurseId: number, timeOffWindow: TimeOffWindow | null, enabled: boolean) => {
	const { trackError } = useAnalytics();
	const windowKey = windowKeyOf(timeOffWindow);
	const debouncedWindowKey = useDebouncedValue(windowKey, WINDOW_DEBOUNCE_MS);
	const isEnabled = enabled && !!departmentId && !!debouncedWindowKey;

	// `enabled` already gates on the key, but the key must keep a stable array shape when disabled.
	const activeWindowKey = debouncedWindowKey ?? NO_WINDOW_KEY;

	const query = useQuery({
		...timeOffImpactQueryOptions(departmentId, nurseId, activeWindowKey, trackError),
		enabled: isEnabled,
		// Never blank the list while a new window loads — the stale answer is dimmed instead.
		placeholderData: keepPreviousData,
	});

	// DEFAULT: "no answer yet" and "answer is an empty list" are rendered the same way ONLY because
	// callers are handed `isLoading`/`isError` alongside and must not present a count while either is
	// true — see `AddDayOffDialog`'s post-shifts label, which suppresses the number rather than
	// showing this 0.
	const shifts = useMemo(() => query.data?.shifts ?? [], [query.data?.shifts]);

	// `overlap: "none"` rows overlap the window but survive the save, so they are excluded from the
	// "affected" list while still counting toward the post-shifts total below.
	const affectedShifts = useMemo<AffectedShift[]>(() => shifts.filter((shift): shift is AffectedShift => shift.overlap !== "none"), [shifts]);

	return {
		affectedShifts,
		overlappingShiftsCount: shifts.length,
		isLoading: query.isLoading && isEnabled,
		isRefreshing: query.isFetching && !query.isLoading,
		isError: query.isError,
	};
};

/**
 * Whether deleting a time off over `timeOffWindow` would have any trimmed/split shift to restore — the
 * question that decides whether the delete dialog offers the restore choice at all.
 *
 * The server owns the segment lookup slack and the flag gate, so the raw PTO window goes over the
 * wire unpadded. While the answer is in flight the caller keeps the control hidden, matching the
 * "nothing to restore" rendering; `isError` is returned so a failed check is told to the user rather
 * than silently collapsing into "nothing to restore".
 */
export const usePtoTrimmedSegments = (departmentId: string, nurseId: number | null, timeOffWindow: TimeOffWindow | null, enabled: boolean) => {
	const { trackError } = useAnalytics();
	const windowKey = windowKeyOf(timeOffWindow);
	const isEnabled = enabled && !!departmentId && nurseId !== null && !!windowKey;

	const query = useQuery<PtoTrimmedSegmentsResponse>({
		queryKey: [PTO_TRIMMED_SEGMENTS_QUERY_KEY_ROOT, departmentId, nurseId, windowKey],
		queryFn: async () => {
			if (nurseId === null || !windowKey) throw new Error("usePtoTrimmedSegments invoked without a nurse or window");
			try {
				const { startIso, endIso } = splitWindowKey(windowKey);
				const { data } = await backendService.getPtoTrimmedSegments(departmentId, nurseId, startIso, endIso);
				return data;
			} catch (error) {
				trackError("pto_trimmed_segments_fetch_error", {
					error_message: error instanceof Error ? error.message : String(error),
					department_id: departmentId,
					nurse_id: nurseId,
				});
				throw error;
			}
		},
		enabled: isEnabled,
	});

	return { hasTrimmedSegments: query.data?.hasTrimmedSegments === true, isLoading: query.isLoading && isEnabled, isError: query.isError };
};

/**
 * Mark every PTO preview stale after a write that created, edited or deleted a time off.
 *
 * Invalidates by prefix so it covers every nurse/window variant at once: both previews describe the
 * schedule the write just changed, and a reopened dialog must not answer from the pre-write cache.
 */
export const useInvalidatePtoImpact = () => {
	const queryClient = useQueryClient();
	return useCallback(
		() =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: [TIME_OFF_IMPACT_QUERY_KEY_ROOT] }),
				queryClient.invalidateQueries({ queryKey: [PTO_TRIMMED_SEGMENTS_QUERY_KEY_ROOT] }),
			]),
		[queryClient],
	);
};
